/**
 * Contract Eligibility — Manage Capital's Segment 2. Which conditions take a contract out of
 * a pool entirely, before any valuation runs.
 *
 * Targets athena_bfsi_prc.prc_contract_eligibility, re-keyed by
 * sql/43_prc_contract_eligibility_by_code.sql from one row per (pool, condition) to one row
 * per (raw BankDwn investor code, condition) — 9 fixed conditions per code, driven by the new
 * athena_bfsi_prc.prc_contract_eligibility_condition reference table. investorCode/
 * capitalType/facilityCode are now display-only — a code maps to a pool through
 * prc_facility_investor_code_map, and a code mapped to none or several pools shows those
 * fields null (facilityLabel falls back to "Code {bankdwnInvestorCode}").
 *
 * Two condition types, two different selections:
 *   List        (Bankruptcy, Risk Rating, PG Flag, Payment Frequency, Asset Type, SIC,
 *                Residual Type) — exclusionValues: the specific values that exclude a
 *                contract. The token "NULL" means "value is missing".
 *   Comparison  (FICO, Contract Term) — exclusionComparisons: {operator, value} pairs, OR'd.
 *
 * Every code always carries exactly these 9 conditions — prc_sync_contract_eligibility()
 * seeds them for any LIVE code that lacks them and never touches an existing one, so there is
 * no create path any more. Only PATCH exists here (status, and the condition's own selection).
 *
 * Direct write, no propose/approve — that table has no governance trail, deliberately, per
 * its own migration header: "a review workflow over a fact with no consequence." See
 * portfolio-risk-concentrations/app/router/eligibility.py's module docstring.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getJson, patchJson, PRC_PREFIX } from "./client";

export type EligibilityConditionType = "List" | "Comparison";
export type ComparisonOperator = ">=" | ">" | "<=" | "<" | "=" | "<>";

export interface EligibilityComparison {
  operator: ComparisonOperator;
  value: number;
}

/** The 9 conditions, in the order Manage Capital's table/dialog show them. Must stay in step
 *  with prc_contract_eligibility_condition's sort_order. */
export const ELIGIBILITY_CONDITIONS = [
  "Bankruptcy",
  "Risk Rating",
  "PG Flag",
  "Payment Frequency",
  "FICO",
  "Asset Type",
  "SIC",
  "Contract Term",
  "Residual Type",
] as const;

export type EligibilityConditionName = (typeof ELIGIBILITY_CONDITIONS)[number];

export const COMPARISON_CONDITIONS: EligibilityConditionName[] = ["FICO", "Contract Term"];

export interface EligibilityCondition {
  eligibilityId: string;
  bankdwnInvestorCode: string;
  investorCode: string | null;
  capitalType: "Debt" | "Equity" | null;
  facilityCode: string | null;
  facilityLabel: string;
  condition: string;
  conditionType: EligibilityConditionType;
  /** Always true on this environment — sql/43_'s model has no "inclusion" mode left. */
  isExclusion: boolean;
  status: "ON" | "OFF";
  /** Set for List conditions, always [] for Comparison ones. */
  exclusionValues: string[];
  /** Set for Comparison conditions, always [] for List ones. */
  exclusionComparisons: EligibilityComparison[];
  updatedAt: string | null;
}

/** The columns the table can be sorted by (the backend's own list). Condition sorts by its rank -- the 1-9 order the conditions are
 *  listed in -- not alphabetically; Status ascending is ON first. */
export type EligibilitySortKey = "investorCode" | "facility" | "condition" | "type" | "selection" | "status";

/** One page of conditions, sorted by `sort` across ALL rows (not just this page) when it is set; the table's own order otherwise. */
export function useEligibilityConditions(page: {
  limit: number;
  offset: number;
  sort?: EligibilitySortKey | null;
  dir?: "asc" | "desc";
}) {
  return useQuery({
    queryKey: ["prc-contract-eligibility", page],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      if (page.sort) {
        q.set("sort", page.sort);
        q.set("dir", page.dir ?? "asc");
      }
      return getJson<{ eligibility: EligibilityCondition[]; total: number }>(
        `${PRC_PREFIX}contract-eligibility?${q.toString()}`,
      );
    },
  });
}

export function useUpdateEligibilityCondition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      eligibilityId: string;
      status?: "ON" | "OFF";
      exclusionValues?: string[];
      exclusionComparisons?: EligibilityComparison[];
    }) =>
      patchJson<{ ok: boolean }>(
        `${PRC_PREFIX}contract-eligibility/${encodeURIComponent(input.eligibilityId)}`,
        {
          status: input.status,
          exclusionValues: input.exclusionValues,
          exclusionComparisons: input.exclusionComparisons,
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prc-contract-eligibility"] }),
  });
}

/** "Y" / "D, F" / "620 or less" / "Not set" — a table-cell summary of what a condition
 *  actually excludes on. */
export function eligibilitySelectionSummary(c: EligibilityCondition): string {
  if (c.conditionType === "List") {
    return c.exclusionValues.length ? c.exclusionValues.join(", ") : "Not set";
  }
  if (!c.exclusionComparisons.length) return "Not set";
  return c.exclusionComparisons.map((cmp) => `${cmp.operator} ${cmp.value}`).join(" or ");
}
